<template>
  <div class="report-page">
    <!-- ========================================== -->
    <!-- هدر -->
    <!-- ========================================== -->
    <div class="page-header">
      <div>
        <h2>📊 گزارش جشنواره</h2>
        <p class="subtitle" v-if="promotion">
          {{ promotion.title }}
          <span v-if="promotion.is_active" class="badge badge-green">فعال</span>
          <span v-else class="badge badge-red">غیرفعال</span>
        </p>
      </div>
      <button class="btn-secondary" @click="goBack">← بازگشت</button>
    </div>

    <!-- ========================================== -->
    <!-- فیلتر تاریخ -->
    <!-- ========================================== -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group">
          <label>از تاریخ</label>
          <input
              v-model="filters.from_date"
              type="date"
              class="filter-input"
              :max="today"
          />
        </div>

        <div class="filter-group">
          <label>تا تاریخ</label>
          <input
              v-model="filters.to_date"
              type="date"
              class="filter-input"
              :max="today"
          />
        </div>

        <button class="btn-primary" @click="loadReport">🔍 اعمال فیلتر</button>
        <button class="btn-reset" @click="resetFilters">⟳ پاک کردن</button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- کارت‌های آمار -->
    <!-- ========================================== -->
    <div v-if="!loading && report" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">📋</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.total_used || 0 }}</span>
          <span class="stat-label">تعداد کل استفاده</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">👤</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.unique_users || 0 }}</span>
          <span class="stat-label">کاربران منحصربه‌فرد</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">✅</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.active_used || 0 }}</span>
          <span class="stat-label">استفاده شده</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">⏳</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.expired_count || 0 }}</span>
          <span class="stat-label">منقضی شده</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">🚫</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.revoked_count || 0 }}</span>
          <span class="stat-label">لغو شده</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">🔄</div>
        <div class="stat-content">
          <span class="stat-value">{{ report.active_count || 0 }}</span>
          <span class="stat-label">فعال (استفاده نشده)</span>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- حالت بارگذاری -->
    <!-- ========================================== -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>در حال بارگذاری گزارش...</span>
    </div>

    <!-- ========================================== -->
    <!-- لیست جزئیات (کارت/جدول) -->
    <!-- ========================================== -->
    <template v-else-if="report && report.logs && report.logs.length > 0">
      <!-- ===== دسکتاپ: جدول ===== -->
      <div class="table-wrapper desktop-table">
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>کاربر</th>
            <th>کد کوپن</th>
            <th>تخفیف</th>
            <th>سفارش</th>
            <th>تاریخ استفاده</th>
            <th>وضعیت</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(log, index) in report.logs" :key="log.id || index">
            <td>{{ index + 1 }}</td>
            <td>
              <span class="user-name">{{ log.user_name || 'کاربر ناشناس' }}</span>
              <span class="user-phone" v-if="log.user_phone">({{ log.user_phone }})</span>
            </td>
            <td>
              <span class="code-badge">{{ log.code || '-' }}</span>
            </td>
            <td>{{ log.discount_text || '-' }}</td>
            <td>#{{ log.order_id || '-' }}</td>
            <td>{{ formatDateTime(log.used_at || log.created_at) }}</td>
            <td>
                <span :class="['badge', getStatusBadge(log)]">
                  {{ getStatusLabel(log) }}
                </span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ===== موبایل: کارت ===== -->
      <div class="mobile-cards">
        <div
            v-for="(log, index) in report.logs"
            :key="log.id || index"
            class="card-item"
        >
          <div class="card-header">
            <span class="card-index">#{{ index + 1 }}</span>
            <span :class="['badge', getStatusBadge(log)]">
              {{ getStatusLabel(log) }}
            </span>
          </div>

          <div class="card-body">
            <div class="card-row">
              <span class="card-label">کاربر:</span>
              <span class="card-value">{{ log.user_name || 'کاربر ناشناس' }}</span>
            </div>
            <div class="card-row" v-if="log.user_phone">
              <span class="card-label">شماره:</span>
              <span class="card-value">{{ log.user_phone }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">کد کوپن:</span>
              <span class="code-value">{{ log.code || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">تخفیف:</span>
              <span class="card-value discount-text">{{ log.discount_text || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">سفارش:</span>
              <span class="card-value">#{{ log.order_id || '-' }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">تاریخ:</span>
              <span class="card-value">{{ formatDateTime(log.used_at || log.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== صفحه‌بندی ===== -->
      <PPagination
          v-if="pagination.total > 0"
          :current-page="pagination.current_page"
          :total-pages="pagination.last_page"
          :total="pagination.total"
          @change-page="handlePageChange"
      />
    </template>

    <!-- ===== حالت خالی ===== -->
    <div v-else-if="!loading" class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>هیچ داده‌ای یافت نشد</h3>
      <p>برای این جشنواره و بازه زمانی انتخاب شده، هیچ استفاده‌ای ثبت نشده است.</p>
    </div>
  </div>
</template>

<script>
import promotionApi from '@/api/promotion';
import PPagination from './PPagination.vue';

export default {
  name: 'PromotionReport',

  components: {
    PPagination
  },

  data() {
    return {
      loading: false,
      promotion: null,
      report: null,
      today: new Date().toISOString().split('T')[0],
      filters: {
        from_date: null,
        to_date: null
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
      }
    };
  },

  mounted() {
    this.loadReport();
  },

  methods: {
    // ==========================================
    // بارگذاری گزارش
    // ==========================================
    async loadReport() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.filters
        };

        // حذف فیلترهای خالی
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === '') {
            delete params[key];
          }
        });

        const { data } = await promotionApi.getPromotionReport(id, params);

        this.promotion = data.promotion || null;
        this.report = data.report || { logs: [] };
        this.pagination = {
          current_page: data.report?.current_page || 1,
          last_page: data.report?.last_page || 1,
          per_page: data.report?.per_page || 15,
          total: data.report?.total || 0
        };
      } catch (error) {
        this.$message?.error('خطا در بارگذاری گزارش') || alert('خطا در بارگذاری گزارش');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // تغییر صفحه
    // ==========================================
    handlePageChange(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.loadReport();
    },

    // ==========================================
    // ریست فیلترها
    // ==========================================
    resetFilters() {
      this.filters = { from_date: null, to_date: null };
      this.pagination.current_page = 1;
      this.loadReport();
    },

    // ==========================================
    // بازگشت
    // ==========================================
    goBack() {
      this.$router.push('/admin/promotions');
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },

    getStatusLabel(log) {
      if (log.is_used) return 'استفاده شده';
      if (log.is_revoked) return 'لغو شده';
      if (log.is_expired) return 'منقضی';
      return 'فعال';
    },

    getStatusBadge(log) {
      if (log.is_used) return 'badge-success';
      if (log.is_revoked) return 'badge-danger';
      if (log.is_expired) return 'badge-warning';
      return 'badge-info';
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.report-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  direction: rtl;
}

/* ========================================== */
/* هدر */
/* ========================================== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.page-header .subtitle {
  margin: 4px 0 0 0;
  color: #606266;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-secondary {
  padding: 8px 20px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-secondary:hover {
  background: #e4e7ed;
}

/* ========================================== */
/* بج‌ها */
/* ========================================== */
.badge {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.badge-green {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-red {
  background: #fef0f0;
  color: #f56c6c;
}

.badge-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.badge-info {
  background: #ecf5ff;
  color: #409eff;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-danger {
  background: #fef0f0;
  color: #f56c6c;
}

/* ========================================== */
/* فیلترها */
/* ========================================== */
.filter-section {
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.filter-input {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  min-width: 150px;
}

.filter-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.btn-primary {
  padding: 6px 20px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #66b1ff;
}

.btn-reset {
  padding: 6px 16px;
  background: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.btn-reset:hover {
  background: #e4e7ed;
}

/* ========================================== */
/* گرید کارت‌های آمار */
/* ========================================== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 14px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.10);
}

.stat-icon {
  font-size: 28px;
  line-height: 1;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 22px;
  font-weight: bold;
  color: #2c3e50;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

/* ========================================== */
/* حالت بارگذاری */
/* ========================================== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  color: #909399;
  gap: 12px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========================================== */
/* حالت خالی */
/* ========================================== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.empty-state p {
  color: #909399;
}

/* ========================================== */
/* جدول دسکتاپ */
/* ========================================== */
.table-wrapper {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 20px;
}

.desktop-table {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.desktop-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 800px;
}

.desktop-table thead th {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  padding: 12px 14px;
  text-align: right;
  border-bottom: 2px solid #e4e7ed;
  white-space: nowrap;
}

.desktop-table tbody td {
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  vertical-align: middle;
}

.desktop-table tbody tr:hover td {
  background: #f5f7fa;
}

.user-name {
  font-weight: 500;
}

.user-phone {
  font-size: 12px;
  color: #909399;
  margin-right: 4px;
}

.code-badge {
  display: inline-block;
  padding: 2px 10px;
  background: #f0f2f5;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: bold;
  color: #2c3e50;
}

/* ========================================== */
/* کارت‌های موبایل */
/* ========================================== */
.mobile-cards {
  display: none;
  padding: 0;
}

.card-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;
}

.card-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.card-index {
  font-weight: 600;
  color: #909399;
  font-size: 13px;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-row {
  display: flex;
  gap: 6px;
  font-size: 13px;
  color: #303133;
  flex-wrap: wrap;
}

.card-label {
  font-weight: 500;
  color: #909399;
  min-width: 60px;
}

.card-value {
  color: #303133;
}

.discount-text {
  font-weight: 500;
  color: #409eff;
}

.code-value {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  font-size: 14px;
  color: #2c3e50;
}

/* ========================================== */
/* صفحه‌بندی */
/* ========================================== */
.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}

@media (max-width: 768px) {
  .report-page {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-secondary {
    align-self: flex-start;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-wrap: wrap;
  }

  .filter-input {
    flex: 1;
    min-width: 80px;
    width: 100%;
  }

  .btn-primary,
  .btn-reset {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 10px;
  }

  .stat-card {
    padding: 12px 14px;
    gap: 10px;
  }

  .stat-icon {
    font-size: 22px;
  }

  .stat-value {
    font-size: 18px;
  }

  /* مخفی کردن جدول دسکتاپ */
  .desktop-table {
    display: none;
  }

  /* نمایش کارت‌ها */
  .mobile-cards {
    display: block;
  }
}

@media (max-width: 480px) {
  .report-page {
    padding: 8px;
  }

  .page-header h2 {
    font-size: 20px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .stat-card {
    padding: 10px 12px;
    flex-direction: column;
    text-align: center;
    gap: 4px;
  }

  .stat-icon {
    font-size: 20px;
  }

  .stat-value {
    font-size: 16px;
  }

  .stat-label {
    font-size: 11px;
  }

  .card-item {
    padding: 12px;
  }

  .card-row {
    font-size: 12px;
  }
}
</style>